import { compact, first, isEmpty, keys, noop, sortBy, uniq } from 'lodash';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { BadgeVariants } from '@/components/notifications/Badge/Badge';
import { useGuardedContext } from '@/hooks/useGuardedContext';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { BalanceSheetTable_BalanceSheetFragment } from '@/modules/balanceSheet/BalanceSheetTable/graphql/BalanceSheetTable.fragments.generated';
import { EducationSlideOverridesProvider } from '@/modules/education/EducationSlideOverrides/EducationSlideOverrides.context';
import { isHypotheticalWaterfall } from '@/modules/estateWaterfall/EstateWaterfall.utils';
import { GrowthProfileModal_AssetClassFragment } from '@/modules/growthProfiles/GrowthProfileModal/graphql/GrowthProfileModal.generated';
import {
  ClientProfile_MissingDocumentFragment,
  HouseholdMissingDocumentFragment,
} from '@/modules/missingDocuments/graphql/MissingDocuments.generated';
import {
  filterEligibleClientProfiles,
  filterNonOverriddenMissingDocuments,
} from '@/modules/missingDocuments/missingDocuments.utils';
import {
  ApprovalKind,
  PresentationApprovalRequirement,
  PresentationBundleKind,
  PresentationPageKind,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';
import { sortAlpha } from '@/utils/sortUtils';

import { ClientPresentationV2Configuration } from './ClientPresentationDesigner.types';
import { getWaterfallWithProjections } from './ClientPresentationDesigner.utils';
import {
  buildPresentationViewSourceMap,
  PresentationViewSourceCitation,
} from './components/presentationEntityActions/presentationViewSource.utils';
import {
  ClientPresentation_BeneficiaryReport_EstateWaterfallBeneficiaryReportFragment,
  ClientPresentation_BeneficiaryReport_EstateWaterfallFragment,
} from './components/slides/BeneficiaryReport/graphql/ClientPresentation.BeneficiaryReport.generated';
import {
  mapDataToTable,
  ProfessionalTeamSlideMember,
} from './components/slides/ProfessionalTeamSlide/ProfessionalTeamSlide.utils';
import {
  ClientPresentationDesignerV2_CashflowFragment,
  ClientPresentationDesignerV2_ComparedWaterfallFragment,
  ClientPresentationDesignerV2_DocumentSummaryPageFragment,
  ClientPresentationDesignerV2_EntitiesOverviewBundleConfigurationEntityFragment,
  ClientPresentationDesignerV2_EntityFragment,
  ClientPresentationDesignerV2_EntityGraphViewBundleConfigurationFragment,
  ClientPresentationDesignerV2_EntityOptionsFragment,
  ClientPresentationDesignerV2_EstateInsightFragment,
  ClientPresentationDesignerV2_EstateWaterfallFragment,
  ClientPresentationDesignerV2_EstateWaterfallOptionsFragment,
  ClientPresentationDesignerV2_GrowthProfileFragment,
  ClientPresentationDesignerV2_HouseholdAssetsSummaryFragment,
  ClientPresentationDesignerV2_HouseholdFragment,
  ClientPresentationDesignerV2_HouseholdMetricsFragment,
  ClientPresentationDesignerV2_LiabilityFragment,
  ClientPresentationDesignerV2Query,
} from './graphql/ClientPresentationDesignerV2.generated';
import { ClientPresentationDesignerV2GrowthProfilesQuery } from './graphql/ClientPresentationDesignerV2GrowthProfiles.generated';
import {
  NEVER_SEEDED_SAVE_STATE,
  PresentationSaveStateHandle,
} from './save/session/presentationSaveState';

export enum ClientPresentationDesignerViewMode {
  Designer = 'DESIGNER',
  Presentation = 'PRESENTATION',
  Print = 'PRINT',
}

interface EntityDetail extends ClientPresentationDesignerV2_EntityFragment {
  deathOrder: {
    id: string;
    displayName: string;
  };
}

export interface ClientPresentationDesignerContextDataShape {
  estateWaterfallBundleConfigurationMap: Record<
    string,
    ClientPresentationDesignerV2_EstateWaterfallFragment
  >;
  beneficiaryReportConfigurationMap: Record<
    string,
    ClientPresentation_BeneficiaryReport_EstateWaterfallBeneficiaryReportFragment
  >;
  beneficiaryReportBundleWaterfallConfigurationMap: Record<
    string,
    ClientPresentation_BeneficiaryReport_EstateWaterfallFragment
  >;
  entityDetailMap: Record<string, EntityDetail>;
  presentationConfiguration: ClientPresentationV2Configuration | null;
  // The kinds of the attestation and approval records for the presentation.
  approvalKinds: ApprovalKind[];
  // The review steps that the presentation must complete. A presentation
  // created before the tenant started to require a step does not need that
  // step, so this can be less than the tenant requirement. Null until the
  // presentation has loaded.
  reviewRequirement: PresentationApprovalRequirement | null;
  professionalTeam: ProfessionalTeamSlideMember[];
  cashflows: ClientPresentationDesignerV2_CashflowFragment[];
  waterfallComparisonBundleConfigurationMap: Record<
    string,
    ClientPresentationDesignerV2_ComparedWaterfallFragment[]
  >;
  // map summary ID to document summary fragment
  documentSummaryMap: Record<
    string,
    ClientPresentationDesignerV2_DocumentSummaryPageFragment
  >;
  // estate insights for the household
  estateInsights: ClientPresentationDesignerV2_EstateInsightFragment[];
  // missing documents
  missingDocuments: HouseholdMissingDocumentFragment[];
  clientProfiles: ClientProfile_MissingDocumentFragment[];
  entityGraphViewMap: Record<
    string,
    ClientPresentationDesignerV2_EntityGraphViewBundleConfigurationFragment['entityGraphView']
  >;
  entitiesOverviewMetricsMap: Record<
    string,
    ClientPresentationDesignerV2_HouseholdMetricsFragment
  >;
  // The configured row entities for each entities-overview bundle, keyed by
  // bundle id. The All Entities slide reads its rows from here (refreshed on
  // save) rather than off the bundle snapshot, which would otherwise stay stale
  // after an entity edit until a full reload.
  entitiesOverviewEntitiesMap: Record<
    string,
    ClientPresentationDesignerV2_EntitiesOverviewBundleConfigurationEntityFragment[]
  >;
  // The household's entities, seeded at load and refreshed by
  // `updateContextData` on save. Surfaces that read an entity's name from
  // context (the designer sidebar tree and the table of contents) key into this
  // by entity id so renames propagate without a full reload.
  entities: ClientPresentationDesignerV2_EntityOptionsFragment[];
}

export interface ClientPresentationDesignerContextShape {
  householdId: string;
  presentationId: string;
  viewMode: ClientPresentationDesignerViewMode;
  /**
   * Whether the current user can edit household data. Computed once here so
   * presentation components can read it from context rather than each calling
   * useCanEditHouseholdData (which throws without an authenticated user — see
   * the print-flow handling where it's computed).
   */
  canUserEditData: boolean;
  /**
   * True while the presentation query is re-running in the background (e.g. a
   * refetch after an edit) but the previous data is still on screen. Distinct
   * from the initial load, which takes over the whole screen.
   */
  isRefreshing: boolean;
  household: ClientPresentationDesignerV2_HouseholdFragment | null;
  householdAssetsSummary: ClientPresentationDesignerV2_HouseholdAssetsSummaryFragment | null;
  assetClasses: GrowthProfileModal_AssetClassFragment[];
  growthProfileMap: Record<
    string,
    ClientPresentationDesignerV2_GrowthProfileFragment
  >;
  liabilities: ClientPresentationDesignerV2_LiabilityFragment[];
  entities: ClientPresentationDesignerV2_EntityOptionsFragment[];
  /**
   * Citations for the household's records — entities, individuals,
   * organizations, and sub-trusts — keyed by record id. Slide hover toolbars key
   * into this to decide whether to offer "View source" and what it opens.
   *
   * Derived from the query rather than held in `contextData` because citations
   * are provenance facts: they don't change as the presentation is edited, so
   * they need neither the autosave refresh nor the machinery that comes with it.
   * A record with nothing to cite has no entry.
   */
  viewSourceMap: Record<string, PresentationViewSourceCitation>;
  balanceSheet: BalanceSheetTable_BalanceSheetFragment | null;
  contextData: ClientPresentationDesignerContextDataShape;
  setContextData: (
    contextData: Partial<ClientPresentationDesignerContextDataShape>
  ) => void;
  /**
   * Refetch the (expensive) presentation query. Call after editing an
   * entity or its holdings so the slides reflect the updated data, since
   * that data is computed server-side and isn't updated by the entity
   * mutation's cache writes.
   */
  refetchPresentation: () => Promise<unknown>;
  /** What the server holds, so a save can write only the difference. */
  saveState: PresentationSaveStateHandle;
  /**
   * Patch a single document summary's rendered content in place. `contextData`
   * is seeded once from the presentation query and isn't re-synced on refetch,
   * and the (expensive) presentation query is also backend-cached against the
   * unchanged presentation — so after editing a summary, call this with the
   * saved values so the slide re-paginates from the new content immediately.
   */
  updateDocumentSummary: (
    summaryId: string,
    patch: { summary: string; displayName: string }
  ) => void;
  estateWaterfallOptionsMap: Record<
    string,
    ClientPresentationDesignerV2_EstateWaterfallOptionsFragment
  >;
  isPersistRunning: boolean;
  setPersistRunning: (isPersistRunning: boolean) => void;
  /** True when a workflow tracker (e.g. presentation template generation) is running */
  isWorkflowGenerating: boolean;
  /**
   * Used by the slide picker to raise the overlay synchronously when a
   * user clicks an AI-generated leaf, and by the persist hook to release
   * the overlay when a `generate: true` save fails before a workflow
   * tracker spawns. Don't use it to drive ongoing generation state — the
   * tracker-completion effects in `ClientPresentationDesigner` own
   * Started→Completed transitions.
   */
  setIsWorkflowGenerating: (isGenerating: boolean) => void;
  /** True when an individual custom slide prompt is being generated via streaming */
  isCustomSlideGenerating: boolean;
  setIsCustomSlideGenerating: (isGenerating: boolean) => void;
  /** Cancel the in-progress custom slide generation. No-op when not generating. */
  cancelCustomSlideGeneration: () => void;
  setCancelCustomSlideGeneration: (cancel: () => void) => void;
}

export const ClientPresentationDesignerContext =
  createContext<ClientPresentationDesignerContextShape>({
    householdId: '',
    presentationId: '',
    viewMode: ClientPresentationDesignerViewMode.Designer,
    canUserEditData: false,
    isRefreshing: false,
    contextData: {
      estateWaterfallBundleConfigurationMap: {},
      beneficiaryReportBundleWaterfallConfigurationMap: {},
      beneficiaryReportConfigurationMap: {},
      entityDetailMap: {},
      presentationConfiguration: null,
      approvalKinds: [],
      reviewRequirement: null,
      professionalTeam: [],
      cashflows: [],
      waterfallComparisonBundleConfigurationMap: {},
      documentSummaryMap: {},
      entityGraphViewMap: {},
      missingDocuments: [],
      clientProfiles: [],
      estateInsights: [],
      entitiesOverviewMetricsMap: {},
      entitiesOverviewEntitiesMap: {},
      entities: [],
    },
    setContextData: noop,
    household: null,
    householdAssetsSummary: null,
    growthProfileMap: {},
    entities: [],
    viewSourceMap: {},
    assetClasses: [],
    liabilities: [],
    balanceSheet: null,
    estateWaterfallOptionsMap: {},
    isPersistRunning: false,
    setPersistRunning: noop,
    isWorkflowGenerating: false,
    setIsWorkflowGenerating: noop,
    isCustomSlideGenerating: false,
    setIsCustomSlideGenerating: noop,
    cancelCustomSlideGeneration: noop,
    setCancelCustomSlideGeneration: noop,
    refetchPresentation: () => Promise.resolve(),
    saveState: NEVER_SEEDED_SAVE_STATE,
    updateDocumentSummary: noop,
  });

export function useClientPresentationDesignerContext() {
  return useGuardedContext(
    ClientPresentationDesignerContext,
    'ClientPresentationDesignerContext'
  );
}

export function useUnguardedClientPresentationDesignerContext() {
  return useContext(ClientPresentationDesignerContext);
}

export function usePresentationConfiguration(): ClientPresentationV2Configuration | null {
  const { contextData } = useClientPresentationDesignerContext();
  return contextData.presentationConfiguration;
}

export function usePresentationApprovalKinds(): ApprovalKind[] {
  const { contextData } = useClientPresentationDesignerContext();
  return contextData.approvalKinds;
}

/**
 * Returns the review steps that the open presentation must complete, or null
 * until the presentation has loaded.
 */
export function usePresentationReviewRequirement(): PresentationApprovalRequirement | null {
  const { contextData } = useClientPresentationDesignerContext();
  return contextData.reviewRequirement;
}

export function useEstateWaterfallSelectOptions(): TypeaheadSelectInputOption<string>[] {
  const { estateWaterfallOptionsMap } = useClientPresentationDesignerContext();
  return useMemo<TypeaheadSelectInputOption<string>[]>(() => {
    return compact(
      keys(
        estateWaterfallOptionsMap
      ).map<TypeaheadSelectInputOption<string> | null>(
        (estateWaterfallId: string) => {
          const waterfall = estateWaterfallOptionsMap[estateWaterfallId];
          if (!waterfall) return null;
          const isHypothetical = isHypotheticalWaterfall(waterfall);

          return {
            display: waterfall.displayName,
            value: estateWaterfallId,
            badgeText: isHypothetical ? 'Hypothetical' : undefined,
            badgeVariant: isHypothetical ? BadgeVariants.Warning : undefined,
          };
        }
      )
    ).sort((a, b) => sortAlpha(a.display, b.display));
  }, [estateWaterfallOptionsMap]);
}

export function useGrowthProfileSelectOptions(): TypeaheadSelectInputOption<string>[] {
  const { growthProfileMap } = useClientPresentationDesignerContext();
  return useMemo<TypeaheadSelectInputOption<string>[]>(() => {
    return compact(
      keys(growthProfileMap).map<TypeaheadSelectInputOption<string> | null>(
        (growthProfileId: string) => {
          const growthProfile = growthProfileMap[growthProfileId];
          if (!growthProfile) return null;
          return {
            display: growthProfile.displayName,
            value: growthProfileId,
            badgeText: growthProfile.isDefault ? 'Default' : undefined,
            badgeVariant: growthProfile.isDefault
              ? BadgeVariants.Primary
              : undefined,
          };
        }
      )
    );
  }, [growthProfileMap]);
}

export function useBundleWaterfall(
  bundleId: string | null | undefined
): ClientPresentationDesignerV2_EstateWaterfallFragment | null {
  const {
    contextData: { estateWaterfallBundleConfigurationMap },
    isRefreshing,
    isPersistRunning,
  } = useClientPresentationDesignerContext();

  // Remember the last resolved waterfall (keyed by bundle id) so we can bridge a
  // transient empty while data is in flight — without ever returning a
  // different bundle's waterfall if the id changes (e.g. a sentinel→real id
  // swap or switching bundles).
  const lastResolvedRef = useRef<{
    bundleId: string;
    waterfall: ClientPresentationDesignerV2_EstateWaterfallFragment;
  } | null>(null);

  const current = bundleId
    ? (estateWaterfallBundleConfigurationMap?.[bundleId] ?? null)
    : null;

  if (current && bundleId) {
    lastResolvedRef.current = { bundleId, waterfall: current };
  }

  // Only bridge to the retained waterfall while the presentation is refetching
  // or persisting, and only for the same bundle — that keeps slides from
  // flashing "No waterfall selected"/blank mid-refresh (e.g. after editing a
  // liability, cash flow, or hypothetical transfer). Once data settles, return
  // the true value so a cleared or changed selection correctly shows the empty
  // state.
  const retained = lastResolvedRef.current;
  if (
    !current &&
    (isRefreshing || isPersistRunning) &&
    retained &&
    retained.bundleId === bundleId
  ) {
    return retained.waterfall;
  }

  return current;
}

/**
 * Gets the waterfall w/ relevant beneficiary report for the bundle.  Note that
 * the report is calculated from a separate endpoint and laid on top of the existing
 * waterfall.
 *
 * @param bundleId - The ID of the bundle to get the beneficiary report waterfall for.
 * @returns The beneficiary report waterfall for the bundle.
 */
export function useBundleBeneficiaryReportWaterfall(
  bundleId: string | null | undefined
): ClientPresentation_BeneficiaryReport_EstateWaterfallFragment | null {
  const {
    contextData: {
      beneficiaryReportBundleWaterfallConfigurationMap: waterfalls,
      beneficiaryReportConfigurationMap: beneficiaryReports,
    },
  } = useClientPresentationDesignerContext();

  if (!bundleId) return null;

  const waterfall = waterfalls?.[bundleId] ?? null;

  if (!waterfall) return null;

  const beneficiaryReport = beneficiaryReports?.[bundleId] ?? null;

  if (!beneficiaryReport) return waterfall;

  return {
    ...waterfall,
    beneficiaryReport,
    // necessary for badges to display as expected
    growthProfile:
      beneficiaryReport.materializedInputParams?.selectedGrowthProfile ??
      waterfall.growthProfile,
  };
}

export function useBundleWaterfallComparison(
  bundleId: string | null | undefined
): ClientPresentationDesignerV2_ComparedWaterfallFragment[] | null {
  const {
    contextData: { waterfallComparisonBundleConfigurationMap },
  } = useClientPresentationDesignerContext();

  if (!bundleId) return null;

  return waterfallComparisonBundleConfigurationMap?.[bundleId] ?? null;
}

export function useBundleEntityGraphView(
  bundleId: string | null | undefined
):
  | ClientPresentationDesignerV2_EntityGraphViewBundleConfigurationFragment['entityGraphView']
  | null {
  const {
    contextData: { entityGraphViewMap },
  } = useClientPresentationDesignerContext();

  if (!bundleId) return null;

  return entityGraphViewMap?.[bundleId] ?? null;
}

export interface UseContextEntityOptions {
  /**
   * Skip the warning if the entity is not found.
   * The document summary standalone bundle does not have an entity,
   * but uses the same slide component as the entity bundle's document
   * summary slide.
   */
  showWarning?: boolean;
}

export function useBundleEntity(
  bundleId: string | null | undefined,
  options?: UseContextEntityOptions
): EntityDetail | null {
  const { showWarning = true } = options || { showWarning: true };
  const {
    contextData: { entityDetailMap },
    isPersistRunning,
  } = useClientPresentationDesignerContext();

  if (!bundleId) return null;

  const entity = entityDetailMap[bundleId];

  if (!entity) {
    if (!isPersistRunning && !isEmpty(entityDetailMap) && showWarning) {
      diagnostics.info(
        `No entity found for bundle ${bundleId} in entityDetailMap`,
        { keys: Object.keys(entityDetailMap) }
      );
    }
    return null;
  }

  return entity;
}

export function useUnguardedBundleEntity(
  bundleId: string | null | undefined
): EntityDetail | null {
  const ctx = useUnguardedClientPresentationDesignerContext();

  if (!bundleId) return null;

  const entity: EntityDetail | undefined =
    ctx?.contextData?.entityDetailMap[bundleId];
  const isPersistRunning = ctx?.isPersistRunning;

  if (!entity) {
    if (!isPersistRunning && !isEmpty(ctx?.contextData?.entityDetailMap)) {
      diagnostics.info(
        `No entity found for bundle ${bundleId} in entityDetailMap`
      );
    }
    return null;
  }

  return entity;
}

export function useDocumentSummaryPage(
  summaryId: string | null | undefined,
  options?: UseContextEntityOptions
): ClientPresentationDesignerV2_DocumentSummaryPageFragment | null {
  const { showWarning = true } = options || { showWarning: true };
  const {
    contextData: { documentSummaryMap },
    isPersistRunning,
  } = useClientPresentationDesignerContext();

  if (!summaryId) return null;

  const documentSummaryPage = documentSummaryMap[summaryId];

  if (!documentSummaryPage) {
    if (!isPersistRunning && !isEmpty(documentSummaryMap) && showWarning) {
      diagnostics.info(
        `No document summary found for summary ${summaryId} in documentSummaryMap`,
        { keys: Object.keys(documentSummaryMap) }
      );
    }
    return null;
  }
  return documentSummaryPage;
}

export interface ClientPresentationDesignerProviderProps {
  householdId: string;
  presentationId: string;
  viewMode: ClientPresentationDesignerViewMode;
  queryData: ClientPresentationDesignerV2Query | undefined | null;
  growthProfilesData:
    | ClientPresentationDesignerV2GrowthProfilesQuery
    | undefined
    | null;
  isWorkflowGenerating?: boolean;
  /** True while a background refetch is in flight (previous data still shown). */
  isRefreshing?: boolean;
  refetchPresentation: () => Promise<unknown>;
  /** Required because saving stops until the form owner seeds server state. */
  saveState: PresentationSaveStateHandle;
  setIsWorkflowGenerating?: (isGenerating: boolean) => void;
}

export function ClientPresentationDesignerProvider({
  children,
  householdId,
  presentationId,
  viewMode,
  queryData,
  growthProfilesData,
  isWorkflowGenerating = false,
  isRefreshing = false,
  refetchPresentation,
  saveState,
  setIsWorkflowGenerating = noop,
}: PropsWithChildren<ClientPresentationDesignerProviderProps>) {
  // Computed once here so presentation components can read it from context. In
  // the print flow there's no authenticated user to check against, so return
  // false instead of throwing; the designer flow always has a user.
  const canUserEditData = useCanEditHouseholdData({
    returnFalseOnMissingUser:
      viewMode === ClientPresentationDesignerViewMode.Print,
  });

  const household = first(getNodes(queryData?.households));
  const householdAssetsSummary = queryData?.householdAssetsSummary ?? null;
  const presentation = first(getNodes(queryData?.clientPresentationV2s));
  const estateWaterfalls = getNodes(queryData?.estateWaterfalls);
  const entities = getNodes(queryData?.entities);
  const balanceSheet = queryData?.balanceSheet ?? null;
  const externalProfessionalTeam = getNodes(queryData?.professionalTeam);
  const externalProfessionalTeamOrganizations = getNodes(
    queryData?.professionalTeamOrganizations
  );
  const cashflows = getNodes(queryData?.cashflows);
  const liabilities = getNodes(queryData?.liabilities);
  const estateInsights = sortBy(getNodes(queryData?.estateInsights), [
    'bucket',
    'position',
  ]);
  const missingDocuments = useMemo(
    () =>
      filterNonOverriddenMissingDocuments(queryData?.householdMissingDocuments),
    [queryData?.householdMissingDocuments]
  );
  const clientProfiles = useMemo(
    () => filterEligibleClientProfiles(queryData?.clientProfiles),
    [queryData?.clientProfiles]
  );

  // Built once per query response. The individual/organization/sub-trust lists are
  // already filtered server-side to records that have an onboarding suggestion, so
  // this is empty for households that never ran AI onboarding — and no "View
  // source" button renders anywhere.
  const viewSourceMap = useMemo(
    () =>
      buildPresentationViewSourceMap({
        entities: getNodes(queryData?.entities),
        clientProfiles: getNodes(queryData?.viewSourceClientProfiles),
        clientOrganizations: getNodes(queryData?.viewSourceClientOrganizations),
        testamentaryEntities: getNodes(
          queryData?.viewSourceTestamentaryEntities
        ),
      }),
    [
      queryData?.entities,
      queryData?.viewSourceClientProfiles,
      queryData?.viewSourceClientOrganizations,
      queryData?.viewSourceTestamentaryEntities,
    ]
  );

  const [isPersistRunning, setPersistRunning] = useState(false);
  const [isCustomSlideGenerating, setIsCustomSlideGenerating] = useState(false);
  const cancelCustomSlideGenerationRef = useRef(noop);
  const cancelCustomSlideGeneration = useCallback(() => {
    cancelCustomSlideGenerationRef.current();
  }, []);
  const setCancelCustomSlideGeneration = useCallback((cancel: () => void) => {
    cancelCustomSlideGenerationRef.current = cancel;
  }, []);

  const growthProfileMap = useMemo(
    () =>
      getNodes(growthProfilesData?.growthProfiles).reduce<
        Record<string, ClientPresentationDesignerV2_GrowthProfileFragment>
      >((acc, growthProfile) => {
        acc[growthProfile.id] = growthProfile;
        return acc;
      }, {}),
    [growthProfilesData?.growthProfiles]
  );

  const estateWaterfallOptionsMap = useMemo(
    () =>
      compact(estateWaterfalls).reduce<
        Record<
          string,
          ClientPresentationDesignerV2_EstateWaterfallOptionsFragment
        >
      >((acc, estateWaterfall) => {
        acc[estateWaterfall.id] = estateWaterfall;
        return acc;
      }, {}),
    [estateWaterfalls]
  );

  const estateWaterfallBundleConfigurationMap = useMemo(() => {
    if (!presentation) {
      return {};
    }
    const bundlesWithWaterfalls = getNodes(presentation.bundles).filter(
      (bundle) => !!bundle.waterfallBundleConfiguration?.estateWaterfall
    );

    return bundlesWithWaterfalls.reduce<
      Record<string, ClientPresentationDesignerV2_EstateWaterfallFragment>
    >((acc, bundle) => {
      const estateWaterfall = getWaterfallWithProjections(
        bundle.waterfallBundleConfiguration
      );

      if (estateWaterfall) {
        acc[bundle.id] = estateWaterfall;
      }
      return acc;
    }, {});
  }, [presentation]);

  const beneficiaryReportBundleWaterfallConfigurationMap = useMemo<
    ClientPresentationDesignerContextShape['contextData']['beneficiaryReportBundleWaterfallConfigurationMap']
  >(() => {
    if (!presentation) {
      return {};
    }
    const bundlesWithBeneficiaryReportBundleConfiguration = getNodes(
      presentation.bundles
    ).filter((bundle) => !!bundle.beneficiariesBundleConfiguration);

    return bundlesWithBeneficiaryReportBundleConfiguration.reduce<
      Record<
        string,
        ClientPresentation_BeneficiaryReport_EstateWaterfallFragment
      >
    >((acc, bundle) => {
      const estateWaterfall =
        bundle.beneficiariesBundleConfiguration?.estateWaterfall;

      if (estateWaterfall) {
        acc[bundle.id] = estateWaterfall;
      }
      return acc;
    }, {});
  }, [presentation]);

  const beneficiaryReportConfigurationMap = useMemo<
    ClientPresentationDesignerContextShape['contextData']['beneficiaryReportConfigurationMap']
  >(() => {
    if (!presentation) {
      return {};
    }
    const bundlesWithBeneficiaryReports = getNodes(presentation.bundles).filter(
      (bundle) =>
        !!bundle.beneficiariesBundleConfiguration
          ?.estateWaterfallBeneficiaryReport
    );

    return bundlesWithBeneficiaryReports.reduce<
      Record<
        string,
        ClientPresentation_BeneficiaryReport_EstateWaterfallBeneficiaryReportFragment
      >
    >((acc, bundle) => {
      const beneficiaryReport =
        bundle.beneficiariesBundleConfiguration
          ?.estateWaterfallBeneficiaryReport;
      if (beneficiaryReport) {
        acc[bundle.id] = beneficiaryReport;
      }
      return acc;
    }, {});
  }, [presentation]);

  const waterfallComparisonBundleConfigurationMap = useMemo(() => {
    if (!presentation) {
      return {};
    }

    const comparisonBundles = getNodes(presentation.bundles).filter(
      (bundle) =>
        bundle.kind === PresentationBundleKind.WaterfallComparisonBundle
    );

    return comparisonBundles.reduce<
      ClientPresentationDesignerContextDataShape['waterfallComparisonBundleConfigurationMap']
    >((acc, bundle) => {
      const comparedWaterfalls =
        bundle.waterfallComparisonBundleConfiguration?.comparedWaterfalls;

      if (comparedWaterfalls && !isEmpty(comparedWaterfalls)) {
        acc[bundle.id] = comparedWaterfalls;
      }
      return acc;
    }, {});
  }, [presentation]);

  const entityDetailMap = useMemo(() => {
    if (!presentation) {
      return {};
    }

    return getNodes(presentation.bundles).reduce<
      ClientPresentationDesignerContextDataShape['entityDetailMap']
    >((acc, bundle) => {
      if (bundle.entitySummaryBundleConfiguration?.entity) {
        const deathOrder = bundle.entitySummaryBundleConfiguration?.deathOrder;
        acc[bundle.id] = {
          ...bundle.entitySummaryBundleConfiguration?.entity,
          deathOrder,
        };
      }
      return acc;
    }, {});
  }, [presentation]);

  const professionalTeam = useMemo(() => {
    if (
      isEmpty(externalProfessionalTeam) &&
      isEmpty(externalProfessionalTeamOrganizations)
    ) {
      return [];
    }

    return mapDataToTable([
      ...externalProfessionalTeam,
      ...externalProfessionalTeamOrganizations,
    ]);
  }, [externalProfessionalTeam, externalProfessionalTeamOrganizations]);

  const assetClasses = useMemo<GrowthProfileModal_AssetClassFragment[]>(() => {
    return getNodes(queryData?.assetClasses);
  }, [queryData]);

  const documentSummaryMap = useMemo<
    ClientPresentationDesignerContextDataShape['documentSummaryMap']
  >(() => {
    const output: ClientPresentationDesignerContextDataShape['documentSummaryMap'] =
      {};

    if (!presentation) {
      return output;
    }

    getNodes(presentation.bundles).forEach(({ pages }) => {
      getNodes(pages)
        .filter(({ kind }) => kind === PresentationPageKind.DocumentSummaryPage)
        .forEach(({ documentSummaryPage }) => {
          const summaryId = documentSummaryPage?.summary?.id;
          if (documentSummaryPage && summaryId && !output[summaryId]) {
            output[summaryId] = documentSummaryPage;
          }
        });
    });

    return output;
  }, [presentation]);

  const entityGraphViewMap = useMemo<
    ClientPresentationDesignerContextDataShape['entityGraphViewMap']
  >(() => {
    if (!presentation) {
      return {};
    }
    const output = getNodes(presentation.bundles).reduce<
      ClientPresentationDesignerContextDataShape['entityGraphViewMap']
    >((acc, bundle) => {
      if (bundle.entityGraphViewBundleConfiguration?.entityGraphView) {
        acc[bundle.id] =
          bundle.entityGraphViewBundleConfiguration.entityGraphView;
      }
      return acc;
    }, {});
    return output;
  }, [presentation]);

  const entitiesOverviewMetricsMap = useMemo<
    ClientPresentationDesignerContextDataShape['entitiesOverviewMetricsMap']
  >(() => {
    if (!presentation) {
      return {};
    }
    const output = getNodes(presentation.bundles).reduce<
      ClientPresentationDesignerContextDataShape['entitiesOverviewMetricsMap']
    >((acc, bundle) => {
      if (bundle.entitiesOverviewBundleConfiguration?.householdMetrics) {
        acc[bundle.id] =
          bundle.entitiesOverviewBundleConfiguration.householdMetrics;
      }
      return acc;
    }, {});
    return output;
  }, [presentation]);

  const entitiesOverviewEntitiesMap = useMemo<
    ClientPresentationDesignerContextDataShape['entitiesOverviewEntitiesMap']
  >(() => {
    if (!presentation) {
      return {};
    }
    return getNodes(presentation.bundles).reduce<
      ClientPresentationDesignerContextDataShape['entitiesOverviewEntitiesMap']
    >((acc, bundle) => {
      if (bundle.entitiesOverviewBundleConfiguration) {
        acc[bundle.id] = getNodes(
          bundle.entitiesOverviewBundleConfiguration.entities
        );
      }
      return acc;
    }, {});
  }, [presentation]);

  const [contextData, setContextDataInternal] =
    useState<ClientPresentationDesignerContextDataShape>({
      estateWaterfallBundleConfigurationMap,
      waterfallComparisonBundleConfigurationMap,
      beneficiaryReportConfigurationMap,
      beneficiaryReportBundleWaterfallConfigurationMap,
      entityDetailMap,
      professionalTeam,
      cashflows,
      documentSummaryMap,
      estateInsights,
      presentationConfiguration: {
        showPageNumbers: presentation?.showPageNumbers ?? false,
        showPageNumbersOnCoverSlide:
          presentation?.showPageNumbersOnCoverSlide ?? false,
        pageNumberToStartFrom: presentation?.pageNumberToStartFrom ?? 1,
        showLegalDisclaimer: presentation?.showLegalDisclaimer ?? false,
        showClientNamesInHeader: presentation?.showClientNamesInHeader ?? true,
      },
      // The schema makes `approvals` required, but a response from before
      // the field existed (for example, a cached or replayed response) does
      // not include it. Treat a missing list as no approvals.
      approvalKinds: uniq(
        (presentation?.approvals ?? []).map(({ kind }) => kind)
      ),
      reviewRequirement: presentation?.reviewRequirement ?? null,
      entityGraphViewMap,
      missingDocuments,
      clientProfiles,
      entitiesOverviewMetricsMap,
      entitiesOverviewEntitiesMap,
      // Seed the live entity list so the sidebar tree / table of contents can
      // read entity names from context; `updateContextData` refreshes it on save.
      entities,
    });

  const setContextData = useCallback(
    (newContextData: Partial<ClientPresentationDesignerContextDataShape>) => {
      setContextDataInternal((prevContextData) => {
        return {
          ...prevContextData,
          ...newContextData,
        };
      });
    },
    [setContextDataInternal]
  );

  const updateDocumentSummary = useCallback<
    ClientPresentationDesignerContextShape['updateDocumentSummary']
  >(
    (summaryId, patch) => {
      setContextDataInternal((prevContextData) => {
        const page = prevContextData.documentSummaryMap[summaryId];
        // Nothing to patch if this summary isn't in the current map.
        if (!page?.summary) {
          return prevContextData;
        }
        return {
          ...prevContextData,
          documentSummaryMap: {
            ...prevContextData.documentSummaryMap,
            [summaryId]: { ...page, summary: { ...page.summary, ...patch } },
          },
        };
      });
    },
    [setContextDataInternal]
  );

  const contextValue: ClientPresentationDesignerContextShape = useMemo(
    () => ({
      householdId,
      presentationId,
      viewMode,
      canUserEditData,
      isRefreshing,
      household: household ?? null,
      householdAssetsSummary,
      growthProfileMap,
      entities,
      viewSourceMap,
      estateWaterfallOptionsMap,
      balanceSheet,
      assetClasses,
      liabilities,
      isPersistRunning,
      setPersistRunning,
      isWorkflowGenerating,
      setIsWorkflowGenerating,
      isCustomSlideGenerating,
      setIsCustomSlideGenerating,
      cancelCustomSlideGeneration,
      setCancelCustomSlideGeneration,
      contextData,
      setContextData,
      refetchPresentation,
      saveState,
      updateDocumentSummary,
    }),
    [
      assetClasses,
      balanceSheet,
      canUserEditData,
      cancelCustomSlideGeneration,
      contextData,
      entities,
      estateWaterfallOptionsMap,
      growthProfileMap,
      household,
      householdAssetsSummary,
      householdId,
      isCustomSlideGenerating,
      isPersistRunning,
      isRefreshing,
      isWorkflowGenerating,
      setIsWorkflowGenerating,
      liabilities,
      presentationId,
      refetchPresentation,
      saveState,
      setCancelCustomSlideGeneration,
      setContextData,
      updateDocumentSummary,
      viewMode,
      viewSourceMap,
    ]
  );

  return (
    <ClientPresentationDesignerContext.Provider value={contextValue}>
      <EducationSlideOverridesProvider
        mode={
          viewMode === ClientPresentationDesignerViewMode.Designer
            ? 'management'
            : 'runtime'
        }
      >
        {children}
      </EducationSlideOverridesProvider>
    </ClientPresentationDesignerContext.Provider>
  );
}
